import React, { useState, useEffect } from 'react';
import { View, StyleSheet, TouchableOpacity, Image, Platform, ActivityIndicator, useWindowDimensions, ScrollView, ViewStyle, TextStyle, ImageStyle, DeviceEventEmitter } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import Ionicons from '@expo/vector-icons/Ionicons';
import { RootStackParamList } from '../navigation/types';
import { Typography, Button, SectionHeader } from './ui';
import { colors, layout, typography } from '../styles';
import { api, getFileUrl } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useAuthCTA } from '../context/AuthCTAContext';
import { useTheme } from '../context/ThemeContext';
import { useI18n } from '../i18n';
import { analytics } from '../services/analytics';
import { StartingSoonItem, StartingSoonResponse, RoomResponse } from '../types/auth';
import { filterPublicRooms } from '../utils/filterPublicRooms';
import { isVisibleInUpcomingFeed } from '../utils/upcomingFeedVisibility';
import { liveSessionCategoryRu, LIVE_SESSION_TERM } from '../utils/liveSessionDisplay';
import { useLivePracticesEnabled, useUnifiedLiveEnabled } from '../hooks/useFeatureFlags';
import { typographyTokens } from '../styles/typographyTokens';

import { ACCENT_TEXT, ON_ACCENT_TEXT } from '../styles/accentText';
// Lives come back as StartingSoonItem; live-practice rooms come back as
// RoomResponse from a different endpoint. We normalise rooms into the
// StartingSoonItem shape and tag them with _kind so navigation /
// "notify" toggles can dispatch correctly.
type UnifiedSoonItem = StartingSoonItem & {
  _kind: 'live' | 'room';
  _inviteCode?: string;
  _numericId?: number;
  _hostUserId?: string;
};

const computeStartsIn = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const diffMs = Date.parse(iso) - Date.now();
  if (diffMs <= 60_000) return 'now';
  const mins = Math.round(diffMs / 60_000);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} h`;
  const days = Math.round(hrs / 24);
  return `${days} d`;
};

const roomToSoonItem = (room: RoomResponse): UnifiedSoonItem => {
  const isBgAudio = room.mediaType === 'BackgroundAudio';
  const durationMin =
    room.durationMinutes
    ?? (isBgAudio && room.soundTrackDurationSeconds ? Math.ceil(room.soundTrackDurationSeconds / 60) : null)
    ?? room.videoPracticeDurationMinutes
    ?? null;
  // AMB-220 named rooms "Live-практика"; CORE-589 collapsed that into the single
  // «Live-сессия» term behind the unified flag, CORE-1865 drops the legacy label
  // altogether: a room is a Live-сессия for the viewer whatever the flag says.
  const category = liveSessionCategoryRu(room.mediaType);
  return {
    id: room.id,
    title: room.title || room.soundTrackTitle || room.videoPracticeTitle || LIVE_SESSION_TERM,
    startDateTimeUtc: room.scheduledAtUtc || room.startedAtUtc || new Date().toISOString(),
    durationMinutes: durationMin,
    coverImageUrl: room.coverImageUrl || room.videoPracticeCoverImageUrl,
    previewUrl: null,
    master: { id: 0, name: room.hostName, photoUrl: room.hostAvatarUrl },
    startsIn: computeStartsIn(room.scheduledAtUtc),
    isRemindersEnabled: room.isSubscribed,
    category,
    _kind: 'room',
    _inviteCode: room.inviteCode,
    _numericId: room.id,
    _hostUserId: room.hostUserId,
  };
};

type NavigationProp = NativeStackNavigationProp<RootStackParamList>;

const HARMONY_PLACEHOLDERS = [
  require('../../assets/generated/cover_morning_vitality.jpg'),
  require('../../assets/generated/agape_bali_yoga_sunrise_v2.jpg'),
  require('../../assets/generated/agape_nature_meditation_lake_v2.jpg'),
];

interface SessionCardProps {
  item: StartingSoonItem;
  index: number;
  onPress: () => void;
  onMasterPress: () => void;
  onNotifyPress: () => void;
  isNotified: boolean;
  cardWidth: number;
}

const SessionCard: React.FC<SessionCardProps> = ({
  item,
  index,
  onPress,
  onMasterPress,
  onNotifyPress,
  isNotified,
  cardWidth,
}) => {
  const { isDark, colors: themeColors } = useTheme();
  const { t } = useI18n('home');
  const isJoinable = item.startsIn.toLowerCase().includes('min') && parseInt(item.startsIn) <= 15;

  // Use API cover image or fallback placeholder
  const coverUrl = getFileUrl(item.coverImageUrl);
  const coverSource = coverUrl ? { uri: coverUrl } : HARMONY_PLACEHOLDERS[index % HARMONY_PLACEHOLDERS.length];

  // Use API master photo or fallback
  const masterPhotoUrl = getFileUrl(item.master.photoUrl);
  const avatarSource = masterPhotoUrl
    ? { uri: masterPhotoUrl }
    : HARMONY_PLACEHOLDERS[index % HARMONY_PLACEHOLDERS.length];

  // Real label from the API. We do NOT invent a category from a fixed
  // fallback array — that used to make duplicate events look like distinct
  // «Психология / Традиция / Философия» variants because the index was
  // derived from `id % 8`.
  const displayCategory = item.category || '';

  return (
    <View style={[
      styles.cardWrapper,
      { width: cardWidth },
      {
        backgroundColor: isDark ? '#121212' : '#FAFAFA',
        borderRadius: 24,
        padding: 6,
        borderWidth: 1,
        borderColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
        ...(Platform.OS === 'web' && {
          boxShadow: isDark ? '0 12px 32px rgba(0,0,0,0.4)' : '0 12px 32px rgba(0,0,0,0.04)',
        })
      }
    ]}>
      <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.9}
        style={[styles.imageContainer, { borderRadius: 18, marginBottom: 4, backgroundColor: isDark ? '#1A1A1A' : '#EFEFEF' }]}
        className={Platform.OS === 'web' ? 'convex-glass-light' : ''}
      >
        <Image source={coverSource} style={styles.image} />
        <View style={styles.overlay} />

        <View style={[
          styles.timeTag, 
          isJoinable 
            ? { backgroundColor: '#E98245' } 
            : { backgroundColor: isDark ? 'rgba(30,30,30,0.95)' : '#FFFFFF' }
        ]} className={Platform.OS === 'web' && !isJoinable ? 'convex-glass-light' : ''}>
          {isJoinable ? (
             <View style={[styles.pulseDot, { backgroundColor: '#FFFFFF', width: 6, height: 6, marginRight: 2 }]} className={Platform.OS === 'web' ? 'pulse-animation' : ''} />
          ) : (
             <Ionicons name="time-outline" size={12} color={themeColors.textPrimary} />
          )}
          <Typography variant="label" style={[
            styles.timeText, 
            isJoinable ? { color: ON_ACCENT_TEXT } : { color: themeColors.textPrimary }
          ]}>
            {item.startsIn.toUpperCase()}
          </Typography>
        </View>
      </TouchableOpacity>

      <View style={[styles.content, { paddingHorizontal: 12, paddingVertical: 14, paddingBottom: 12 }]}>
        <TouchableOpacity onPress={onPress} activeOpacity={0.7}>
          {/* Reserve 2-line height (lineHeight 24 × 2 = 48) so single-line
              and two-line titles take the same vertical space — the bottom
              CTA lands on the same baseline across cards. Does not change
              data, navigation, or section visibility. */}
          <Typography variant="bodyAccent" style={[styles.sessionTitle, { color: themeColors.textPrimary, minHeight: 48 }]} numberOfLines={2}>{item.title}</Typography>
        </TouchableOpacity>

        <View style={styles.metaRowSingle}>
          <TouchableOpacity onPress={onMasterPress} activeOpacity={0.7} style={styles.masterInfo}>
            <Image source={avatarSource} style={styles.avatar} />
            <View style={{ flex: 1 }}>
              <Typography variant="bodyAccent" style={[styles.masterName, { color: themeColors.textPrimary }]} numberOfLines={1}>{item.master.name}</Typography>
              {displayCategory ? (
                <Typography variant="caption" style={[styles.durationLabel, { color: themeColors.textPrimary }]} numberOfLines={1}>{displayCategory}</Typography>
              ) : null}
            </View>
          </TouchableOpacity>
        </View>

        {/* Full-width join CTA — sits below the master row.
            States:
              - Live (isJoinable): orange + play icon + «Войти»
              - Notified (isNotified): «active» colour (TBD per design) + «Иду»
              - Default: charcoal in light theme / subtle white tint in
                dark theme so the button stays visible against the card.
            Background colours intentionally inline so swapping the
            «Иду» colour later is a one-line change. */}
        <TouchableOpacity
          onPress={isJoinable ? onPress : onNotifyPress}
          style={[
            styles.joinCtaFull,
            isJoinable
              ? { backgroundColor: '#E98245' }
              : isNotified
                ? { backgroundColor: '#E98245' }
                : isDark
                  ? { backgroundColor: 'rgba(255,255,255,0.08)', borderWidth: 1, borderColor: 'rgba(255,255,255,0.1)' }
                  : { backgroundColor: '#0F1419' },
          ]}
          hitSlop={{ top: 6, bottom: 6, left: 6, right: 6 }}
          activeOpacity={0.85}
        >
          {isJoinable && (
            <Ionicons name="play" size={14} color={ON_ACCENT_TEXT} style={{ marginRight: 6 }} />
          )}
          <Typography variant="label" style={[styles.joinCtaFullText, { color: ON_ACCENT_TEXT }]}>
            {isJoinable ? t('startingSoon.joinNowBtn', 'Войти') : (isNotified ? 'Иду' : 'Я пойду')}
          </Typography>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const MIN_LIVE_THRESHOLD = 1;

const COVER_IMAGES = {
  cover1: require('../../assets/generated/agape_bali_yoga_sunrise_v2.jpg'),
  cover2: require('../../assets/generated/cover_2.png'),
  cover3: require('../../assets/generated/agape_nature_meditation_lake_v2.jpg'),
  cover4: require('../../assets/generated/agape_bali_yoga_sunrise_v2.jpg'),
  cover5: require('../../assets/generated/cover_3.png'),
};

export const StartingSoon: React.FC = () => {
  const navigation = useNavigation<NavigationProp>();
  const { isAuthenticated } = useAuth();
  const { showAuthCTA } = useAuthCTA();
  const { isDark, colors: themeColors } = useTheme();
  const { t } = useI18n('home');
  // AMB-231 — gate the "live practice rooms" leg of the merged feed.
  // With the flag off the Hero strip still renders Live-сессии / Offline,
  // it just stops mixing in scheduled / live-now Practice rooms.
  const livePracticesEnabled = useLivePracticesEnabled();
  const unifiedLive = useUnifiedLiveEnabled();
  const [sessions, setSessions] = useState<UnifiedSoonItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [notifiedIds, setNotifiedIds] = useState<Set<string>>(new Set());

  const { width: windowWidth } = useWindowDimensions();
  const isDesktop = windowWidth >= 1024;
  const isMobileView = windowWidth < 768;
  const paddingH = isMobileView ? 12 : (Platform.OS === 'web' ? 48 : 24);
  const listGap = 16;
  const maxVisibleDesktop = 4;
  const visibleCount = Math.max(1, Math.min(maxVisibleDesktop, sessions.length || 4));
  const desktopWidth = (windowWidth - (paddingH * 2) - (listGap * (visibleCount - 1))) / visibleCount;
  // Mobile width logic:
  //   - 1 session → card fills the viewport (no point in showing peek
  //     of empty space; the «slim card on left» reads as broken).
  //   - 2+ sessions → 320px so the right edge of the second card peeks
  //     in and signals «swipe me».
  const cardWidth = isDesktop
    ? desktopWidth
    : (sessions.length <= 1 ? windowWidth - (paddingH * 2) : 320);

  const showLiveMode = sessions.length >= MIN_LIVE_THRESHOLD;

  useEffect(() => {
    fetchSessions();
    const liveSub = DeviceEventEmitter.addListener('live_published', fetchSessions);
    const roomSub = DeviceEventEmitter.addListener('room_created', fetchSessions);
    return () => {
      liveSub.remove();
      roomSub.remove();
    };
    // AMB-231 — re-fetch when the flag flips so the feed reflects the
    // new gate without a page reload. CORE-589 — also re-map on the unified
    // flag so cards pick up the «Live-сессия» vocabulary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [livePracticesEnabled, unifiedLive]);

  const fetchSessions = async () => {
    try {
      setLoading(true);
      // /api/home/starting-soon returns only Lives (LiveStream + Offline).
      // Live-practice rooms live behind a separate pair of endpoints; merge
      // them in so the home strip matches what Practices/Скоро shows.
      const now = Date.now();
      // AMB-231 — skip the two room endpoints entirely when the flag is
      // off. Saves two HTTP round-trips per Hero mount and guarantees no
      // room ever leaks into the feed via a race where the array fetched
      // but the gate ran on the *previous* render's flag value.
      const [livesRes, liveNowRooms, soonRooms] = await Promise.all([
        api.getStartingSoon(10).catch(() => ({ items: [], totalCount: 0 } as StartingSoonResponse)),
        livePracticesEnabled
          ? api.getHomeLiveNowRooms(6).catch(() => [] as RoomResponse[])
          : Promise.resolve([] as RoomResponse[]),
        livePracticesEnabled
          ? api.getHomeStartingSoonRooms(6).catch(() => [] as RoomResponse[])
          : Promise.resolve([] as RoomResponse[]),
      ]);

      const liveItems: UnifiedSoonItem[] = (livesRes.items || []).map(it => ({ ...it, _kind: 'live' }));

      const seenInvites = new Set<string>();
      const roomItems: UnifiedSoonItem[] = [];
      for (const r of filterPublicRooms([...(liveNowRooms || []), ...(soonRooms || [])])) {
        if (!isVisibleInUpcomingFeed(r.scheduledAtUtc, now)) continue;
        if (seenInvites.has(r.inviteCode)) continue;
        seenInvites.add(r.inviteCode);
        roomItems.push(roomToSoonItem(r));
      }

      const combined = [...liveItems, ...roomItems]
        .sort((a, b) => {
          const ta = Date.parse(a.startDateTimeUtc || '') || Number.MAX_SAFE_INTEGER;
          const tb = Date.parse(b.startDateTimeUtc || '') || Number.MAX_SAFE_INTEGER;
          return ta - tb;
        })
        .slice(0, 10);

      setSessions(combined);
      const notified = combined.filter(s => s.isRemindersEnabled).map(s => String(s.id));
      setNotifiedIds(new Set(notified));
    } catch (err) {
      console.error('Error fetching starting soon:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleNotify = async (item: UnifiedSoonItem) => {
    analytics.track('home_notify_clicked', { user_state: isAuthenticated ? 'user' : 'guest', trigger_source: 'starting_soon' });
    if (!isAuthenticated) { showAuthCTA('gated_action', 'Sign up to get notified before sessions start'); return; }
    const sessionId = String(item.id);
    const isOn = notifiedIds.has(sessionId);
    try {
      if (item._kind === 'room') {
        const roomId = item._numericId;
        if (!roomId) return;
        if (isOn) await api.unsubscribeFromRoom(roomId);
        else await api.subscribeToRoom(roomId);
      } else {
        if (isOn) await api.disableLiveReminders(item.id);
        else await api.enableLiveReminders(item.id);
      }
      setNotifiedIds(prev => {
        const n = new Set(prev);
        if (isOn) n.delete(sessionId); else n.add(sessionId);
        return n;
      });
    } catch (err) { console.error(err); }
  };

  if (!showLiveMode) return null;

  return (
    <View style={[styles.section, { backgroundColor: themeColors.canvas }]}>
      <View style={styles.container}>
        <SectionHeader
          title={t('startingSoon.title')}
          actionText={t('common:buttons.viewAll')}
          onActionPress={() => { analytics.track('home_view_all_live_streams_clicked'); navigation.navigate('Lives'); }}
        />
        {loading ? (
          <View style={{ paddingVertical: 60, alignItems: 'center' }}>
            <ActivityIndicator size="small" color={ACCENT_TEXT} />
          </View>
        ) : (
          <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={[styles.list, { paddingRight: isDesktop ? 0 : 24 }]}>
            {sessions.map((item, idx) => (
              <SessionCard
                key={`${item._kind}-${item.id}`}
                item={item}
                index={idx}
                onPress={() => {
                  analytics.track('home_event_clicked', { event_id: item.id, event_status: 'starting_soon', trigger_source: 'starting_soon', kind: item._kind });
                  if (item._kind === 'room' && item._numericId) {
                    navigation.navigate('RoomAnnouncement' as any, { roomId: item._numericId });
                  } else {
                    navigation.navigate('LiveDetail', { id: item.id, masterProfileId: item.master.id });
                  }
                }}
                onMasterPress={() => {
                  if (item._kind === 'room' && item._hostUserId) {
                    navigation.navigate('PublicUserProfile' as any, { userId: item._hostUserId });
                  } else {
                    navigation.navigate('MasterDetail', { masterId: item.master.id });
                  }
                }}
                onNotifyPress={() => handleNotify(item)}
                isNotified={notifiedIds.has(String(item.id))}
                cardWidth={cardWidth}
              />
            ))}
          </ScrollView>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  section: {
    paddingTop: Platform.OS === 'web' ? 40 : 32,
    paddingBottom: Platform.OS === 'web' ? 40 : 32,
    backgroundColor: 'transparent',
  } as ViewStyle,
  container: {
    maxWidth: layout.maxWidth,
    marginHorizontal: 'auto',
    paddingHorizontal: layout.paddingH,
    width: '100%',
  } as ViewStyle,
  header: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    marginBottom: 40,
  } as ViewStyle,
  title: {
    // CORE-1118: was overridden with 'Inter' — a family the app doesn't ship.
    // The section title is a display heading, so the approved token stands.
    ...typography.sectionTitle,
  } as TextStyle,
  viewAll: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    padding: 12,
  } as ViewStyle,
  viewAllText: {
    ...typographyTokens.label,
  } as TextStyle,
  emptyState: {
    paddingVertical: 60,
    alignItems: 'center',
  } as ViewStyle,
  emptyText: {
    marginTop: 16,
    opacity: 0.5,
    textAlign: 'center',
  } as TextStyle,
  emptyLink: {
    marginTop: 16,
  } as ViewStyle,
  list: {
    gap: 16,
    paddingRight: 80,
  } as ViewStyle,
  cardWrapper: {
    width: 340,
  } as ViewStyle,
  imageContainer: {
    width: '100%',
    aspectRatio: 1.25,
    borderRadius: 24,
    overflow: 'hidden',
    backgroundColor: '#F7F6F4',
    marginBottom: 14,
  } as ViewStyle,
  image: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  } as ImageStyle,
  overlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.03)',
  } as ViewStyle,
  timeTag: {
    position: 'absolute',
    top: 12,
    left: 12,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 100,
    backgroundColor: '#FFFFFF',
    ...(Platform.OS === 'web' && { boxShadow: '0 2px 8px rgba(0,0,0,0.10)' }),
  } as any,
  timeText: {
    ...typographyTokens.label,
    color: '#2B2730',
  } as TextStyle,
  joinableTag: {
    position: 'absolute',
    bottom: 16,
    left: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 100,
  } as ViewStyle,
  pulseDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#2B2730',
  } as ViewStyle,
  joinableText: {
    ...typographyTokens.label,
    color: '#2B2730',
  } as TextStyle,
  content: {
    paddingHorizontal: 4,
  } as ViewStyle,
  sessionTitle: {
    ...typographyTokens.bodyAccent,
    color: '#2B2730',
    marginBottom: 12,
  } as TextStyle,
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  } as ViewStyle,
  metaRowSingle: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  } as ViewStyle,
  joinCtaFull: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 12,
    width: '100%',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'background-color 0.18s ease, transform 0.08s ease',
    } as any),
  } as any,
  joinCtaFullText: {
    ...typographyTokens.label,
  } as TextStyle,
  masterInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  } as ViewStyle,
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: '#eee',
  } as ImageStyle,
  masterName: {
    ...typographyTokens.bodyAccent,
    color: '#2B2730',
  } as TextStyle,
  durationLabel: {
    ...typographyTokens.caption,
    color: '#2B2730',
    opacity: 0.5,
    marginTop: 2,
  } as TextStyle,
  calendarBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 100,
  } as ViewStyle,
  calendarBtnActive: {
    backgroundColor: '#E98245',
  } as ViewStyle,
  calendarText: {
    ...typographyTokens.label,
    opacity: 0.9,
  } as TextStyle,
});
